

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { VideoPlayerRiv } from "@/components/VideoPlayer/VideoPlayerRiv";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { IconRefresh, IconArrowLeft } from "@tabler/icons-react";
import { currentLocationNext, withNext } from "@/lib/auth/safeNext";

type ErrorProps = {
  error: string;
};

/**
 * P3 (FG2): error classification. Upstream SSR pages previously passed
 * `error.message` verbatim; here we match the common shapes against
 * known classes so the rendered copy + affordance matches the
 * actual failure. Unknown strings fall back to the generic
 * "failedInitialisation" copy with a retry button.
 */
type ErrorClass = "notFound" | "forbidden" | "unauthorised" | "network" | "server" | "unknown";

function classifyError(raw: string): ErrorClass {
  if (!raw) return "unknown";
  const lower = raw.toLowerCase();
  // Direct error codes (from backend registry).
  if (lower === "eventnotfound" || lower === "notfound" || lower.includes("not found")) {
    return "notFound";
  }
  // PB-CP-10: `eventplanlocked` belongs here beside its archived twin. It used
  // to fall through to "unknown", whose CTA is RELOAD — offered for a 403 that
  // will never stop being a 403, so the customer was told the application
  // broke and handed a button that loops. The `forbidden` class says what
  // happened and offers "Go back".
  //
  // A plan-locked SPEAKERS/SPONSORS read no longer produces this at all
  // (PB-PA-3 opened those reads), so the specific screen that was observed is
  // gone. The classification gap is not: any 403 carrying this code — a write
  // refusal surfaced through an error boundary, or any read still gated — is
  // still misfiled by a classifier that has no entry for it. One line, and it
  // cannot be wrong: a 403 is not "unknown".
  //
  // `insufficientCapability` is the same story one wave later (audit
  // 2026-09-28, M2): the code a role WITHOUT `event-goals:read` receives from
  // the goals endpoint, surfaced through the page's error screen — a 403 that
  // stays a 403, so the reload button looped here too.
  if (
    lower === "forbidden"
    || lower === "eventarchived"
    || lower === "eventplanlocked"
    || lower === "insufficientpermissions"
    || lower === "insufficientcapability"
  ) {
    return "forbidden";
  }
  if (lower === "unauthorised" || lower === "unauthorized" || lower === "missing token" || lower === "invalid token") {
    return "unauthorised";
  }
  if (lower.includes("network") || lower.includes("fetch failed") || lower === "econnrefused") {
    return "network";
  }
  // HTTP status suffix pattern from handleResponse.
  if (/\b5\d{2}\b/.test(raw)) return "server";
  if (/\b404\b/.test(raw)) return "notFound";
  if (/\b401\b/.test(raw)) return "unauthorised";
  if (/\b403\b/.test(raw)) return "forbidden";
  return "unknown";
}

export const SError: React.FC<ErrorProps> = ({ error }) => {
  const t = useTranslations("errors");

  useEffect(() => {
    window.dispatchEvent(new Event("navigationEnd"));
  }, []);

  const cls = classifyError(error);
  const headline = cls === "unknown"
    ? t("somethingWentWrong")
    : t.has(`${cls}Title`)
      ? t(`${cls}Title`)
      : t("somethingWentWrong");
  const description = cls === "unknown"
    ? t("somethingWentWrongDescription")
    : t.has(`${cls}Description`)
      ? t(`${cls}Description`)
      : t("somethingWentWrongDescription");
  // Affordance per class: `unauthorised` → log in again; a gone/forbidden
  // resource → "go back" (reloading would just re-hit the same 404/403);
  // everything else → retry.
  const isGoBack = cls === "notFound" || cls === "forbidden";
  const ctaLabel = cls === "unauthorised"
    ? t("logInAgain")
    : isGoBack
      ? t("goBack")
      : t("reload");
  const ctaIcon = isGoBack ? IconArrowLeft : IconRefresh;
  const ctaAction = cls === "unauthorised"
    ? () => {
        // Back here after the login (billing-comms Teil 2a): the page rides
        // along as a validated `next`. Straight to `/auth/login` — the bare
        // `/login` alias used to drop the query on the way.
        window.location.href = withNext("/auth/login", currentLocationNext());
      }
    : isGoBack
      ? () => {
          // Prefer the in-app history; fall back to the root if this tab opened
          // straight onto the dead resource (no prior entry to return to).
          if (window.history.length > 1) window.history.back();
          else window.location.href = "/";
        }
      : () => {
          window.location.reload();
        };

  return (
    <div
      data-testid="s-error"
      data-error-class={cls}
      className="flex flex-col items-center justify-center h-full w-full grow py-16 gap-4"
    >
      <VideoPlayerRiv videoName="NotFound" width="w-30 sm:w-35 lg:w-40" height="h-40 sm:h-50 lg:h-60" />
      <div className="flex flex-col items-center gap-2 max-w-sm">
        <h2 className="body-1-bold font-primary text-center">{headline}</h2>
        <p className="body-2-regular font-tertiary text-center">{description}</p>
        {/* PB-CP-10: the specific backend message used to render ONLY for the
            `unknown` class. Moving `eventPlanLocked` into `forbidden` would
            therefore have fixed the CTA and LOST the one sentence the customer
            needs — "your plan has expired" — trading one bad screen for
            another. So the rule is now "show the code's own copy whenever we
            have copy for it", which is true of every class; `unknown` keeps
            its generic fallback because there, by definition, we do not. */}
        {(t.has(error) || cls === "unknown") && (
          <p className="body-3-medium font-error text-center mt-1">
            {t.has(error) ? t(error) : t("failedInitialisation")}
          </p>
        )}
      </div>
      <PrimaryButton
        text={ctaLabel}
        icon={ctaIcon}
        size="small"
        width="w-max"
        onClick={ctaAction}
      />
    </div>
  );
};
